import { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Pagination,
  Avatar,
  Button,
  Divider,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Search as SearchIcon, Book as BookIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { translations } from '../translations';

interface Book {
  id: number;
  title: string;
  cover_image_url: string;
}

interface Author {
  id: number;
  name: string;
  name_ku: string | null;
  author_image_url: string | null;
  biography: string;
  biography_ku: string;
  birth_date: string;
  book_count: number;
  books: Book[] | null;
}

const AuthorsList = () => {
  const [authors, setAuthors] = useState<Author[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const language = localStorage.getItem('language') || 'ku';
  const isRtl = language === 'ku';
  const t = isRtl ? translations.ku : translations.en;

  useEffect(() => {
    const fetchAuthors = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`/api/authors?page=${page}&limit=20&search=${searchQuery}`);
        setAuthors(response.data.authors);
        setTotalPages(response.data.totalPages);
      } catch (error) {
        console.error('Error fetching authors:', error);
      }
      setLoading(false);
    };

    const debounceTimer = setTimeout(() => {
      fetchAuthors();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [page, searchQuery]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(1);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 500 }}>
          {t.menuAuthors}
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          placeholder={t.searchAuthors}
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            maxWidth: 500,
            '& .MuiOutlinedInput-root': {
              bgcolor: 'background.paper',
            },
          }}
        />
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={3}>
            {authors.map((author) => (
              <Grid item key={author.id} xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: 'background.paper',
                    '&:hover': {
                      boxShadow: 6,
                    },
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                      <Avatar
                        sx={{
                          width: 80,
                          height: 80,
                          bgcolor: theme.palette.primary.main,
                          mr: 2,
                          fontSize: '2rem',
                          component: 'Link',
                          href: `/authors/${author.id}`,
                        }}
                      >
                        {author.author_image_url ? (
                          <img src={author.author_image_url.startsWith('http') ? author.author_image_url : `/images/${author.author_image_url}`} alt={author.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          author.name.charAt(0)
                        )}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h6"
                          component={Link}
                          to={`/authors/${author.id}`}
                          sx={{
                            textDecoration: 'none',
                            color: 'inherit',
                            '&:hover': {
                              color: 'primary.main',
                            },
                          }}
                        >
                          {isRtl ? author.name_ku : author.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, mb: 1 }}>
                          <BookIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {author.book_count} {author.book_count === 1 ? t.book : t.books}
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{ mb: 2 }}
                        >
                          {t.followAuthor}
                        </Button>
                      </Box>
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        height: '5em',
                      }}
                    >
                      {isRtl 
                        ? (author?.biography_ku && author?.biography_ku.length > 0 
                            ? author?.biography_ku 
                            : t.noBiography)
                        : (author?.biography && author?.biography.length > 0 
                            ? author?.biography 
                            : t.noBiography)
                      }
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t.latestBooks}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {author.books?.slice(0, 3).map((book) => (
                        <Typography
                          key={book.id}
                          variant="body2"
                          component={Link}
                          to={`/books/${book.id}`}
                          sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            cursor: 'pointer',
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          {book.title}
                        </Typography>
                      ))}
                      {author.books && author.books.length > 3 && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          +{author.books.length - 3} {t.moreBooks}
                        </Typography>
                      )}
                      {!author.books && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          {t.noBooksAvailable}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              size="large"
            />
          </Box>
        </>
      )}
    </Container>
  );
};

export default AuthorsList; 